import type { AthenaPrincipal } from "../../../runtime/data/principal.ts";
import {
  AthenaBillingCapabilityError,
  AthenaBillingProviderRequestError,
  isAthenaBillingCapabilityError,
  isAthenaBillingProviderRequestError,
} from "../../errors.ts";
import type { BillingProviderConfigMap } from "../../providers/types.ts";
import { prepareBillingCommand } from "../../safety/prepare.ts";
import { isBillingSelfPlanChangeEnabled } from "../../self-enrollment.ts";
import { resolveBillingConnectionForOperation } from "../../subject/connection-resolver.ts";
import { billingSubjectNotFound } from "../../subject/errors.ts";
import { requireBillingPrincipalUserId } from "../../subject/principal-user.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";
import type { BillingSubscription } from "../../types.ts";
import { PROCESS_BILLING_INVOCATION } from "../invocation-authority.ts";
import { executeLocalBillingPriceList } from "../local/execute/price-list.ts";
import {
  executeLocalBillingSubscriptionCancel,
  executeLocalBillingSubscriptionCreate,
  executeLocalBillingSubscriptionGet,
  executeLocalBillingSubscriptionUpdate,
} from "../local/execute/subscriptions.ts";
import type { BillingProviderRegistry } from "../local/providers/registry.ts";
import { resolveBillingCheckoutIntent } from "./checkout-intent.ts";
import { selfDelegatedBillingPrincipal } from "./delegated-principal.ts";
import { persistOwnedSubscription } from "./enroll.ts";
import { ensureActiveCoordinatorForSubscription } from "./enrollment-reservation.ts";
import {
  beginPlanChangeOperation,
  completePlanChange,
  failPlanChange,
  transitionPlanChange,
} from "./plan-change-saga.ts";
import { getSelfSubscription } from "./subscriptions.ts";

const LOAD_OWNED_SUBSCRIPTION_VERSION_SQL = `
SELECT id, row_version, updated_at
FROM billing.billing_subscriptions
WHERE id = $1
  AND subject_kind = $2
  AND subject_id = $3
  AND ownership_status = 'resolved'
LIMIT 1
`;

const UPDATE_OWNED_SUBSCRIPTION_SQL = `
UPDATE billing.billing_subscriptions
SET amount_currency = $4,
    amount_value = $5,
    interval = $6,
    description = $7,
    metadata = COALESCE(metadata, '{}'::jsonb) || $8::jsonb,
    row_version = row_version + 1,
    updated_at = now()
WHERE id = $1
  AND subject_kind = $2
  AND subject_id = $3
  AND ownership_status = 'resolved'
  AND row_version = $9
RETURNING *
`;

function mollieInterval(interval: string): string {
  const trimmed = interval.trim();
  if (/^\d+\s+\S/.test(trimmed)) {
    return trimmed;
  }
  return `1 ${trimmed}`;
}

export function isProviderSubscriptionUpdateUnsupported(
  error: unknown
): boolean {
  if (isAthenaBillingCapabilityError(error)) {
    return (
      error.reason === "unsupported_by_provider" ||
      error.reason === "provider_operation_unsupported"
    );
  }
  if (isAthenaBillingProviderRequestError(error)) {
    return error.kind === "unsupported_operation";
  }
  if (error instanceof AthenaBillingCapabilityError) {
    return (
      error.reason === "unsupported_by_provider" ||
      error.reason === "provider_operation_unsupported"
    );
  }
  if (error instanceof AthenaBillingProviderRequestError) {
    return error.kind === "unsupported_operation";
  }
  return false;
}

function isUncertainProviderOutcome(error: unknown): boolean {
  if (isAthenaBillingProviderRequestError(error)) {
    return (
      error.retry === "reconcile_first" ||
      error.kind === "timeout" ||
      error.kind === "network" ||
      error.kind === "provider_unavailable"
    );
  }
  return false;
}

export async function changeSelfSubscription(input: {
  configuredProviders?: BillingProviderConfigMap;
  currentVersion?: string;
  idempotencyKey: string;
  planChangeEnabled?: boolean;
  principal: AthenaPrincipal;
  priceId: string;
  proration?: "none" | "immediate";
  registry?: BillingProviderRegistry;
  sql: BillingSqlExecutor;
  subscriptionId?: string;
  testMode?: boolean;
}): Promise<BillingSubscription> {
  if (!isBillingSelfPlanChangeEnabled(input.planChangeEnabled)) {
    throw new AthenaBillingCapabilityError({
      operation: "self.subscription.change",
      reason: "self_enrollment_disabled",
    });
  }
  const command = prepareBillingCommand({
    operation: "self.subscription.change",
    payload: {
      currentVersion: input.currentVersion,
      idempotencyKey: input.idempotencyKey,
      priceId: input.priceId,
      proration: input.proration,
      subscriptionId: input.subscriptionId,
    },
  });
  if (input.registry == null) {
    throw new AthenaBillingCapabilityError({
      operation: "self.subscription.change",
      reason: "runtime_unavailable",
    });
  }
  const subjectId = requireBillingPrincipalUserId(input.principal);
  const current = await getSelfSubscription({
    principal: input.principal,
    sql: input.sql,
    subscriptionId: input.subscriptionId,
  });
  if (current.status === "canceled") {
    throw billingSubjectNotFound("No active subscription to change.");
  }
  if (current.id == null || current.id.length === 0) {
    throw billingSubjectNotFound("No active subscription to change.");
  }
  const ownedSubscriptionId = current.id;
  const versionRow = await input.sql.query(
    LOAD_OWNED_SUBSCRIPTION_VERSION_SQL,
    [ownedSubscriptionId, "user", subjectId]
  );
  const rowVersion = Number(versionRow.rows[0]?.row_version ?? 1);
  if (
    input.currentVersion != null &&
    input.currentVersion.trim() !== "" &&
    input.currentVersion !== String(rowVersion)
  ) {
    throw new AthenaBillingCapabilityError({
      message: "Subscription version does not match the current plan.",
      operation: "self.subscription.change",
      reason: "unsupported_operation",
    });
  }
  const prices = await executeLocalBillingPriceList({
    authority: PROCESS_BILLING_INVOCATION,
    configuredProviders: input.configuredProviders,
    payload: {},
    principal: selfDelegatedBillingPrincipal(input.principal, ["prices.list"]),
    registry: input.registry,
    testMode: input.testMode,
  });
  const price = prices.items.find(
    (item) => item.id === String(command.payload.priceId)
  );
  if (price == null) {
    throw new AthenaBillingCapabilityError({
      operation: "self.subscription.change",
      reason: "missing_catalog",
    });
  }
  const intent = resolveBillingCheckoutIntent(price);
  if (intent.kind !== "recurring") {
    throw new AthenaBillingCapabilityError({
      message: "Plan changes require a recurring catalog price.",
      operation: "self.subscription.change",
      reason: "unsupported_operation",
    });
  }
  const connectionId = await resolveBillingConnectionForOperation({
    operation: "self.subscription.change",
    sql: input.sql,
    testMode: input.testMode,
  });
  const coordinator = await ensureActiveCoordinatorForSubscription({
    connectionId,
    idempotencyKey: `coordinator:${current.id}`,
    priceId: price.id,
    providerSubscriptionId: current.providerSubscriptionId,
    sql: input.sql,
    subjectId,
    subjectKind: "user",
  });
  if (coordinator.state !== "active") {
    throw new AthenaBillingCapabilityError({
      message: "A pending enrollment must finish before changing plan.",
      operation: "self.subscription.change",
      reason: "unsupported_operation",
    });
  }
  const operation = await beginPlanChangeOperation({
    connectionId,
    enrollmentId: coordinator.id,
    expectedRowVersion: rowVersion,
    idempotencyKey: input.idempotencyKey,
    ownedSubscriptionId,
    priceId: price.id,
    sql: input.sql,
    subjectId,
    subjectKind: "user",
  });
  const leaseToken = operation.leaseToken;
  if (leaseToken == null) {
    throw new AthenaBillingCapabilityError({
      message: "Another plan change is in progress.",
      operation: "self.subscription.change",
      reason: "unsupported_operation",
    });
  }
  const metadata = {
    athenaPlanChange: true,
    athenaSubjectId: subjectId,
    effectiveAt: new Date().toISOString(),
    newPriceId: price.id,
    oldPriceId:
      typeof current.metadata === "object" &&
      current.metadata != null &&
      "priceId" in current.metadata
        ? String((current.metadata as { priceId?: unknown }).priceId ?? "")
        : "",
    proration: input.proration ?? "none",
    providerOperations: ["subscriptions.update"],
  };
  try {
    const updated = await executeLocalBillingSubscriptionUpdate({
      authority: PROCESS_BILLING_INVOCATION,
      configuredProviders: input.configuredProviders,
      payload: {
        amount: price.amount,
        customerId: current.providerCustomerId,
        description: `Athena plan ${price.id}`,
        interval: mollieInterval(intent.interval),
        metadata,
        subscriptionId: current.providerSubscriptionId,
      },
      principal: selfDelegatedBillingPrincipal(input.principal, [
        "subscriptions.update",
      ]),
      registry: input.registry,
      testMode: input.testMode,
    });
    const persisted = await input.sql.query(UPDATE_OWNED_SUBSCRIPTION_SQL, [
      current.id,
      "user",
      subjectId,
      price.amount.currency,
      price.amount.value,
      intent.interval,
      updated.description ?? `Athena plan ${price.id}`,
      JSON.stringify(metadata),
      rowVersion,
    ]);
    if (!persisted.rows[0]) {
      throw new AthenaBillingCapabilityError({
        message: "Subscription version does not match the current plan.",
        operation: "self.subscription.change",
        reason: "unsupported_operation",
      });
    }
    await completePlanChange({
      id: operation.id,
      leaseToken,
      sql: input.sql,
    });
    return {
      ...current,
      ...updated,
      amount: price.amount,
      description: updated.description ?? `Athena plan ${price.id}`,
      id: current.id,
    };
  } catch (updateError) {
    if (isUncertainProviderOutcome(updateError)) {
      await executeLocalBillingSubscriptionGet({
        authority: PROCESS_BILLING_INVOCATION,
        configuredProviders: input.configuredProviders,
        payload: {
          customerId: current.providerCustomerId,
          subscriptionId: current.providerSubscriptionId,
        },
        principal: selfDelegatedBillingPrincipal(input.principal, [
          "subscriptions.get",
        ]),
        registry: input.registry,
        testMode: input.testMode,
      });
      await failPlanChange({
        id: operation.id,
        lastError: "provider_outcome_unknown",
        leaseToken,
        sql: input.sql,
      });
      throw updateError;
    }
    if (!isProviderSubscriptionUpdateUnsupported(updateError)) {
      await failPlanChange({
        id: operation.id,
        lastError:
          updateError instanceof Error
            ? updateError.message
            : "subscription_update_failed",
        leaseToken,
        sql: input.sql,
      });
      throw updateError;
    }
    const replacement = await executeLocalBillingSubscriptionCreate({
      authority: PROCESS_BILLING_INVOCATION,
      configuredProviders: input.configuredProviders,
      payload: {
        amount: price.amount,
        customerId: current.providerCustomerId,
        description: `Athena plan ${price.id}`,
        idempotencyKey: `${input.idempotencyKey}:replacement`,
        interval: mollieInterval(intent.interval),
        metadata,
      },
      principal: selfDelegatedBillingPrincipal(input.principal, [
        "subscriptions.create",
      ]),
      registry: input.registry,
      testMode: input.testMode,
    });
    const recorded = await transitionPlanChange({
      expectedState: "requested",
      id: operation.id,
      leaseToken,
      replacementProviderSubscriptionId: replacement.providerSubscriptionId,
      sql: input.sql,
      state: "replacement_created",
    });
    if (recorded == null) {
      throw new AthenaBillingCapabilityError({
        message: "Plan change lease was lost.",
        operation: "self.subscription.change",
        reason: "unsupported_operation",
      });
    }
    try {
      await executeLocalBillingSubscriptionCancel({
        authority: PROCESS_BILLING_INVOCATION,
        configuredProviders: input.configuredProviders,
        payload: {
          customerId: current.providerCustomerId,
          subscriptionId: current.providerSubscriptionId,
        },
        principal: selfDelegatedBillingPrincipal(input.principal, [
          "subscriptions.cancel",
        ]),
        registry: input.registry,
        testMode: input.testMode,
      });
    } catch (cancelError) {
      await transitionPlanChange({
        expectedState: "replacement_created",
        id: operation.id,
        lastError:
          cancelError instanceof Error
            ? cancelError.message
            : "old_cancel_failed",
        leaseToken,
        sql: input.sql,
        state: "compensating",
      });
      try {
        await executeLocalBillingSubscriptionCancel({
          authority: PROCESS_BILLING_INVOCATION,
          configuredProviders: input.configuredProviders,
          payload: {
            customerId: current.providerCustomerId,
            subscriptionId: replacement.providerSubscriptionId,
          },
          principal: selfDelegatedBillingPrincipal(input.principal, [
            "subscriptions.cancel",
          ]),
          registry: input.registry,
          testMode: input.testMode,
        });
      } finally {
        await failPlanChange({
          id: operation.id,
          lastError: "compensated_replacement_after_old_cancel_failure",
          leaseToken,
          sql: input.sql,
        });
      }
      throw cancelError;
    }
    await transitionPlanChange({
      expectedState: "replacement_created",
      id: operation.id,
      leaseToken,
      sql: input.sql,
      state: "old_cancelled",
    });
    const persisted = await persistOwnedSubscription({
      connectionId,
      interval: intent.interval,
      operation: "self.subscription.change",
      principal: input.principal,
      sql: input.sql,
      subscription: replacement,
    });
    await input.sql.query(
      `UPDATE billing.billing_subscriptions
			 SET status = 'canceled', canceled_at = now(), row_version = row_version + 1
			 WHERE id = $1 AND subject_id = $2 AND row_version = $3`,
      [current.id, subjectId, rowVersion]
    );
    await completePlanChange({
      id: operation.id,
      leaseToken,
      sql: input.sql,
    });
    return persisted;
  }
}
